import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useTrilogyContext } from '../../context';
import { hashClass } from '../../helpers';
import { is } from '../../services';
import { Input, InputAutoCompleteType } from '../input';
import { defaultMatching, getLabel } from './Autocomplete.helpers';
import AutoCompleteItem from './item';
import AutoCompleteMenu from './menu';
import { debounce } from './utils';
/**
 * AutoComplete Component
 * @param placeholder {string} placeholder for input
 * @param defaultValue {string} Default Value for Input
 * @param data {string[]} Datas AutoComplete list Item
 * @param inputValue {string} Value of Input
 * @param onChange {Function} OnChange Input Event
 * @param children {Function} Custom Component for dropdown list
 * @param displayMenu {boolean} Display Autocomplete Menu (default: true)
 * @param matching {Function} matching function
 * - ------------------ WEB PROPERTIES -----------------------
 * @param classNameMenu {string} Additionnal CSS Classes for Menu
 * @param absoluteMenu {boolean} Absolute position for Menu
 * @param fullwidthMenu {boolean} Fullwidth size for Menu
 * @param className {string} Additionnal CSS Classes
 * @param value {string} Value for Input
 * @param onItemSelected {Function} OnSelectedItemList event
 * @param customIcon {string} Additional icon classes
 * @param debounceSuggestionsTimeout {number} Timeout for getSuggestions debounce
 */
const AutoComplete = ({ defaultValue, value, classNameMenu, absoluteMenu, fullwidthMenu, placeholder, data, status, onBlur, testId, onChange, name, matching = defaultMatching, displayMenu = true, onItemSelected, customIcon, reference, inputValue, disabled, children, accessibilityLabel, onIconClick, getSuggestions, debounceSuggestionsTimeout, onFocus, loading }) => {
    var _a;
    const { styled } = useTrilogyContext();
    const [itemSelected, setItemSelected] = useState(null);
    const [_inputValue, setInputValue] = useState(inputValue !== null && inputValue !== void 0 ? inputValue : '');
    const [_value, setValue] = useState((_a = placeholder !== null && placeholder !== void 0 ? placeholder : defaultValue) !== null && _a !== void 0 ? _a : '');
    const [activeItem, setActiveItem] = useState(0);
    const [isAutocompleteMenuVisible, setIsAutocompleteMenuVisible] = useState(displayMenu || false);
    const [search, setSearch] = useState([]);
    const autocompleteClasses = hashClass(styled, clsx(is('autocomplete'), is('active')));
    useEffect(() => {
        setInputValue(inputValue || '');
    }, [inputValue]);
    useEffect(() => {
        setActiveItem(0);
        if (data === null || data === void 0 ? void 0 : data.length) {
            if (matching) {
                setSearch(matching(data, _inputValue));
            }
        }
        if (itemSelected && getLabel(itemSelected) !== _inputValue) {
            setItemSelected(null);
        }
    }, [_inputValue]);
    useEffect(() => {
        var _a, _b;
        setValue((_b = (_a = placeholder !== null && placeholder !== void 0 ? placeholder : value) !== null && _a !== void 0 ? _a : defaultValue) !== null && _b !== void 0 ? _b : '');
    }, [value, defaultValue]);
    useEffect(() => {
        setSearch(matching(data, _inputValue));
    }, [data]);
    const onTextChanged = async (e) => {
        setIsAutocompleteMenuVisible(true);
        if (onChange) {
            onChange({ inputName: name || '', inputValue: e.inputValue, inputSelectionStart: null });
        }
        // Check if the input value is a regular expression or a string
        if (e.inputValue.startsWith('/') && e.inputValue.endsWith('/')) {
            try {
                const pattern = e.inputValue.substring(1, e.inputValue.lastIndexOf('/'));
                setInputValue(new RegExp(pattern, 'i').source);
            }
            catch (error) {
                // If the regular expression is invalid, keep the input value as a string
                setInputValue(e.inputValue);
            }
        }
        else {
            setInputValue(e.inputValue);
        }
        if (getSuggestions) {
            const data = await getSuggestions(e.inputValue);
            setSearch(data);
        }
        else if (matching && data) {
            setSearch(matching(data, e.inputValue));
        }
    };
    const onInputChange = debounceSuggestionsTimeout ? debounce(onTextChanged, debounceSuggestionsTimeout) : onTextChanged;
    const suggestionSelected = (value, data, search) => {
        setIsAutocompleteMenuVisible(false);
        setInputValue(getLabel(value));
        setItemSelected(value);
        if (data || search) {
            if (onItemSelected) {
                onItemSelected({
                    value: value,
                    index: value ? (data.length ? data : search).indexOf(value) : -1,
                });
            }
        }
    };
    const handleKeyPress = (e) => {
        switch (e.inputKeyCode) {
            case 38:
                if (activeItem === 0)
                    return;
                setActiveItem(activeItem - 1);
                break;
            case 40:
                if (activeItem === search.length - 1)
                    return;
                setActiveItem(activeItem + 1);
                break;
            case 13:
                if (search[activeItem] != null) {
                    suggestionSelected(search[activeItem], data, search);
                }
                break;
            default:
        }
    };
    const handleFocus = (event) => {
        setIsAutocompleteMenuVisible(true);
        if (onFocus)
            onFocus(event);
    };
    return (React.createElement("div", { className: hashClass(styled, clsx('control')) },
        React.createElement(Input, { accessibilityLabel: accessibilityLabel, ...(customIcon ? { customIcon: customIcon } : {}), reference: reference, placeholder: _value, ...(name ? { name: name } : {}), className: 'autocomplete-input', type: 'text', testId: testId, status: status, autoCompleteType: InputAutoCompleteType.OFF, disabled: disabled, 
            // Add delay for selection of suggestion
            onBlur: (e) => {
                setTimeout(() => setIsAutocompleteMenuVisible(false), 250);
                if (onBlur)
                    onBlur(e);
            }, onFocus: handleFocus, onKeyUp: handleKeyPress, value: _inputValue, onChange: (event) => {
                onInputChange(event);
            }, onIconClick: onIconClick, loading: loading }),
        isAutocompleteMenuVisible && (React.createElement("div", { className: autocompleteClasses }, search.length > 0 && (React.createElement(AutoCompleteMenu, { testId: testId, absolute: absoluteMenu, fullwidth: fullwidthMenu, className: classNameMenu }, search.map((item, i) => (React.createElement(AutoCompleteItem, { active: activeItem === i, key: i, testId: testId, item: item, suggestionSelected: (v) => suggestionSelected(v, data, search) }, children ? children(item) : getLabel(item)))))))),
        React.createElement("div", null)));
};
export default AutoComplete;
//# sourceMappingURL=AutoComplete.js.map